import { School } from '@mui/icons-material';
import { Autocomplete, Avatar, Box, TextField } from '@mui/material';
import { useCommunityContext } from 'providers';
import { useMemo } from 'react';

import { CommunityFragment, useCommunitiesQuery } from 'shared/graphql';
import { recordError } from 'shared/utils/record';

export interface CommunitySelectProps {
  /** Label for the input. */
  label?: string;

  /** Whether the input should be disabled. */
  disabled?: boolean;

  /** Whether the input should take up the full width of the container. */
  fullWidth?: boolean;

  /** Whether the input should be required. */
  required?: boolean;
}

export function CommunitySelect({
  label = 'Community',
  disabled = false,
  fullWidth = true,
  ...props
}: CommunitySelectProps) {
  const {
    selectedCommunity,
    selectCommunity,
    loading: isCommunitiesLoading,
    selecting: isSelecting,
  } = useCommunityContext();

  const { data, loading: isListLoading } = useCommunitiesQuery({
    variables: { sort: [{ name: 'ASC' }] },
    onError: recordError,
  });

  const communities = useMemo(() => {
    const communities = data?.communities.items ?? [];
    // Filter out the selected community to avoid showing it as an option
    return communities.filter(community => community.id && community.id !== selectedCommunity?.id);
  }, [data]);

  const extractCommunityName = (community: CommunityFragment): string => {
    const name = community.name || community.fullName || community.id;
    return name as string;
  };

  const handleCommunityChange = (community: CommunityFragment | null) => {
    if (!community?.id) {
      return;
    }

    selectCommunity(community?.id);
  };

  const isLoading = isListLoading || isCommunitiesLoading || isSelecting;

  return (
    <Autocomplete
      options={communities}
      value={selectedCommunity}
      onChange={(_, community) => handleCommunityChange(community)}
      getOptionLabel={extractCommunityName}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      disableClearable={Boolean(selectedCommunity)}
      fullWidth={fullWidth}
      disabled={disabled || isLoading}
      loading={isLoading}
      loadingText="Loading communities…"
      noOptionsText="No communities found."
      renderInput={params => <TextField {...props} {...params} label={label} variant="outlined" />}
      renderOption={(props, community) => {
        const name = extractCommunityName(community);
        const logo = community.images?.logo?.downloadUrl ?? undefined;

        return (
          <Box component="li" {...props} key={community.id} sx={{ gap: 1.5 }}>
            <Avatar src={logo} alt={name} variant="rounded">
              <School fontSize="inherit" />
            </Avatar>

            {name}
          </Box>
        );
      }}
    />
  );
}
